import React from 'react';
import { ApolloError, type FetchResult } from '@apollo/client';
import { messageFromError } from '@oysterjs/core/graphql/errors';

import { client } from '~/api/client';
import {
  AddCaraWorkspaceArtifactCommentDocument,
  type AddCaraWorkspaceArtifactCommentMutation,
  type AddCaraWorkspaceArtifactCommentMutationVariables,
  AddCaraWorkspaceMessageDocument,
  type AddCaraWorkspaceMessageMutation,
  type AddCaraWorkspaceMessageMutationVariables,
  type CaraWorkspaceArtifactCommentFieldsFragment,
  DeleteCaraWorkspaceArtifactCommentDocument,
  type DeleteCaraWorkspaceArtifactCommentMutation,
  type DeleteCaraWorkspaceArtifactCommentMutationVariables,
  ResolveCaraWorkspaceArtifactCommentDocument,
  type ResolveCaraWorkspaceArtifactCommentMutation,
  type ResolveCaraWorkspaceArtifactCommentMutationVariables,
  useGetCaraWorkspaceArtifactCommentsQuery
} from '~/api/graphql';

import { type CommentDraft, isRegionDraft } from '~/components/workspaces/lib/comment-draft';

export type ArtifactComment = CaraWorkspaceArtifactCommentFieldsFragment;

export const isOpenComment = (comment: ArtifactComment) => !comment.resolvedAt;

/** What a click on "Send to Cara" asks of Cara. The conversation shows the comments instead. */
export const SEND_COMMENTS_MESSAGE = 'Work through the comments on this document.';

const failureMessage = (error: unknown) => messageFromError(error, 'Something went wrong');
const reloadAfterSaveFailure =
  'Your change was saved, but the comments list could not be reloaded.';
const noComments: ArtifactComment[] = [];

/**
 * The comments on one document, with the calls that change them. Every change re-reads the
 * list rather than patching it, because a comment's placement is derived on the server from
 * the document as it reads now. A new comment names the version on screen, so its words are
 * looked for in the document the reader was actually reading.
 */
export function useArtifactComments(params: {
  workspaceId: string;
  artifactId: string;
  artifactVersion: number;
}) {
  const { workspaceId, artifactId, artifactVersion } = params;
  const query = useGetCaraWorkspaceArtifactCommentsQuery({
    variables: { workspaceId, artifactId },
    fetchPolicy: 'cache-and-network'
  });
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const { refetch } = query;

  const run = React.useCallback(
    async (change: () => Promise<FetchResult>) => {
      setBusy(true);
      setError(null);
      try {
        const result = await change();
        if (result.errors?.length) throw new ApolloError({ graphQLErrors: result.errors });
        // The comment is saved even if reloading the list fails. Returning false here
        // would keep the draft and offer to add the same comment a second time.
        await refetch().catch(() => setError(reloadAfterSaveFailure));
        return true;
      } catch (e) {
        setError(failureMessage(e));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [refetch]
  );

  const add = React.useCallback(
    (draft: CommentDraft, body: string) =>
      run(() =>
        client.mutate<
          AddCaraWorkspaceArtifactCommentMutation,
          AddCaraWorkspaceArtifactCommentMutationVariables
        >({
          mutation: AddCaraWorkspaceArtifactCommentDocument,
          variables: {
            workspaceId,
            artifactId,
            input: isRegionDraft(draft)
              ? { region: draft.region, version: artifactVersion, body }
              : { ...draft.candidate, version: artifactVersion, body }
          }
        })
      ),
    [run, workspaceId, artifactId, artifactVersion]
  );

  const resolve = React.useCallback(
    (commentId: string) =>
      run(() =>
        client.mutate<
          ResolveCaraWorkspaceArtifactCommentMutation,
          ResolveCaraWorkspaceArtifactCommentMutationVariables
        >({
          mutation: ResolveCaraWorkspaceArtifactCommentDocument,
          variables: { workspaceId, commentId }
        })
      ),
    [run, workspaceId]
  );

  const remove = React.useCallback(
    (commentId: string) =>
      run(() =>
        client.mutate<
          DeleteCaraWorkspaceArtifactCommentMutation,
          DeleteCaraWorkspaceArtifactCommentMutationVariables
        >({
          mutation: DeleteCaraWorkspaceArtifactCommentDocument,
          variables: { workspaceId, commentId }
        })
      ),
    [run, workspaceId]
  );

  const send = React.useCallback(
    () =>
      run(() =>
        client.mutate<AddCaraWorkspaceMessageMutation, AddCaraWorkspaceMessageMutationVariables>({
          mutation: AddCaraWorkspaceMessageDocument,
          variables: {
            id: workspaceId,
            input: {
              message: SEND_COMMENTS_MESSAGE,
              files: [],
              processCommentsArtifactId: artifactId
            }
          }
        })
      ),
    [run, workspaceId, artifactId]
  );

  const comments = query.data?.caraWorkspaceArtifactComments ?? noComments;
  const open = React.useMemo(() => comments.filter(isOpenComment), [comments]);
  return {
    comments,
    open,
    loading: query.loading,
    busy,
    error: error ?? (query.error ? failureMessage(query.error) : null),
    retry: () => {
      setError(null);
      return query.refetch().catch((e: unknown) => setError(failureMessage(e)));
    },
    add,
    resolve,
    remove,
    send
  };
}
